/**
 * Sessie 52b: alles uit een gesprek is terug te lezen (punt 4 van 26-09).
 *
 * Drie stukken die de middenkolom gebruikt:
 *
 * - `BallonExtra`: onder een beurt de verzendstatus (altijd als woord,
 *   nooit alleen een kleur), het uitgeschreven spraakbericht en de
 *   foto's met wat Nova erop zag.
 * - `SchadeBlok`: het schadedossier van de zaak in gewone taal.
 * - `BeveiligdeFoto`: een foto achter de inlog. Het adres vraagt een
 *   token dat alleen axios meestuurt; een losse `<img src>` krijgt een
 *   401. Daarom halen we de bytes als blob op en tonen we een
 *   object-adres, dat we bij het weghalen weer vrijgeven.
 */
import { useEffect, useState } from "react";

import { adminChatApi, emailsApi } from "@/lib/api";
import type {
  SchadeControle,
  Schadedossier,
  ZaakBallon,
  ZaakBallonFoto,
} from "@/lib/werkplek-api";

import { datumTijd } from "./tijd";

/** Opent de vergrotingslaag van de middenkolom. */
export type OpFoto = (url: string, naam: string) => void;

const VERZENDSTATUS_TEKST: Record<string, string> = {
  verzonden: "Verzonden",
  bezorgd: "Bezorgd",
  gelezen: "Gelezen",
  mislukt: "Mislukt",
};

/** De verzendstatus als woord; een onbekende waarde leesbaar gemaakt. */
export function verzendstatusTekst(status: string): string {
  const bekend = VERZENDSTATUS_TEKST[status];
  if (bekend) return bekend;
  const leesbaar = status.replace(/_/g, " ").trim();
  return leesbaar.charAt(0).toUpperCase() + leesbaar.slice(1);
}

/** Haalt de bytes van een foto: uit een chatbericht of uit een mail. */
function haalFoto(foto: ZaakBallonFoto): Promise<Blob> {
  if (foto.bron === "mail" && foto.email_id) {
    return emailsApi.bijlageInhoud(foto.email_id, foto.bericht_id);
  }
  return adminChatApi.bijlage(foto.bericht_id);
}

/**
 * Laadt een foto achter de inlog als blob en geeft een object-adres.
 * `null` zolang hij laadt, `"fout"` als hij niet op te halen is.
 */
function useFotoAdres(foto: ZaakBallonFoto): string | null | "fout" {
  const [adres, setAdres] = useState<string | null | "fout">(null);
  const berichtId = foto.bericht_id;
  const bron = foto.bron;
  const emailId = foto.email_id;
  useEffect(() => {
    let weg = false;
    let objectAdres: string | null = null;
    setAdres(null);
    haalFoto({ bericht_id: berichtId, bron, email_id: emailId, naam: "", url: "" })
      .then((blob) => {
        if (weg) return;
        objectAdres = URL.createObjectURL(blob);
        setAdres(objectAdres);
      })
      .catch(() => {
        if (!weg) setAdres("fout");
      });
    return () => {
      weg = true;
      if (objectAdres) URL.revokeObjectURL(objectAdres);
    };
  }, [berichtId, bron, emailId]);
  return adres;
}

export function BeveiligdeFoto({
  foto,
  opFoto,
}: {
  foto: ZaakBallonFoto;
  opFoto: OpFoto;
}) {
  const adres = useFotoAdres(foto);
  const geladen = adres !== null && adres !== "fout";
  const uitMail = foto.bron === "mail";
  return (
    <figure className="wp-bfoto" data-foto={foto.bericht_id}>
      <button
        type="button"
        className="wp-foto"
        disabled={!geladen}
        title={
          adres === "fout"
            ? uitMail
              ? "Deze foto staat in de mail van de klant. Open de mail om hem te zien."
              : "Deze foto is niet op te halen. Ververs de pagina of lees het origineel."
            : undefined
        }
        onClick={() => geladen && opFoto(adres, foto.naam)}
      >
        {geladen ? (
          <img src={adres} alt={foto.naam} />
        ) : (
          <span style={{ position: "static" }}>
            {adres === "fout"
              ? uitMail
                ? "Foto staat in de mail"
                : "Foto niet op te halen"
              : "Foto laden…"}
          </span>
        )}
      </button>
      {foto.blik ? (
        <figcaption className="wp-bfoto-blik">
          <b>Nova zag:</b> {foto.blik}
        </figcaption>
      ) : (
        <figcaption className="wp-bfoto-blik wp-leegregel">
          Nova heeft deze foto nog niet bekeken.
        </figcaption>
      )}
    </figure>
  );
}

/** Onder een beurt: verzendstatus, spraak uitgeschreven en foto's. */
export function BallonExtra({
  ballon,
  opFoto,
}: {
  ballon: ZaakBallon;
  opFoto: OpFoto;
}) {
  const fotos = ballon.fotos ?? [];
  return (
    <>
      {ballon.transcript ? (
        <span className="wp-ballon-spraak" data-transcript>
          <b>Spraakbericht, uitgeschreven:</b> {ballon.transcript}
        </span>
      ) : null}
      {fotos.length > 0 ? (
        <span className="wp-ballon-fotos">
          {fotos.map((foto) => (
            <BeveiligdeFoto key={foto.bericht_id} foto={foto} opFoto={opFoto} />
          ))}
        </span>
      ) : null}
    </>
  );
}

/** De verzendstatus als chip met woord, voor in de voet van de ballon. */
export function VerzendStatus({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  return (
    <span
      className="wp-verzendstatus"
      data-verzendstatus={status}
      title="Verzendstatus van WhatsApp"
    >
      {verzendstatusTekst(status)}
    </span>
  );
}

function ControleRegel({ controle }: { controle: SchadeControle }) {
  return (
    <li className="wp-schade-controle" data-uitkomst={controle.uitkomst}>
      <span className="wp-schade-controle-naam">{controle.naam}</span>
      <span className="wp-schade-uitkomst" data-uitkomst={controle.uitkomst}>
        {controle.uitkomst_tekst}
      </span>
      {controle.reden ? (
        <span className="wp-schade-reden">{controle.reden}</span>
      ) : null}
    </li>
  );
}

/** Het blok Schade: wat Nova zag, de controles, keuzes en de stand. */
export function SchadeBlok({
  dossier,
  opFoto,
}: {
  dossier: Schadedossier;
  opFoto: OpFoto;
}) {
  return (
    <div className="wp-blok wp-schade" data-schadedossier>
      <h4>
        Schade
        <span className="wp-koprechts">
          <span className="wp-chip wp-chip-grijs" data-stand={dossier.stand}>
            {dossier.stand_tekst}
          </span>
        </span>
      </h4>
      <div className="wp-schade-binnen">
        {dossier.ordernummer || dossier.zwaarte || dossier.moment ? (
          <p className="wp-schade-sub">
            {[
              dossier.ordernummer ? `Order ${dossier.ordernummer}` : null,
              dossier.zwaarte,
              dossier.moment ? `gemeld ${datumTijd(dossier.moment)}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}

        {dossier.fotos.length > 0 ? (
          <div className="wp-ballon-fotos">
            {dossier.fotos.map((foto) => (
              <BeveiligdeFoto key={foto.bericht_id} foto={foto} opFoto={opFoto} />
            ))}
          </div>
        ) : (
          <p className="wp-leegregel">De klant heeft nog geen foto gestuurd.</p>
        )}

        <h5>Wat Nova op de foto zag</h5>
        {dossier.wat_nova_zag.length > 0 ? (
          <ul className="wp-schade-lijst" data-nova-zag>
            {dossier.wat_nova_zag.map((regel, i) => (
              <li key={i}>{regel}</li>
            ))}
          </ul>
        ) : (
          <p className="wp-leegregel">Nova heeft de foto nog niet beoordeeld.</p>
        )}

        <h5>Controles</h5>
        {dossier.controles.length > 0 ? (
          <ul className="wp-schade-lijst wp-schade-controles">
            {dossier.controles.map((controle, i) => (
              <ControleRegel key={`${controle.naam}-${i}`} controle={controle} />
            ))}
          </ul>
        ) : (
          <p className="wp-leegregel">De controles zijn nog niet gedaan.</p>
        )}

        <h5>Aangeboden keuzes</h5>
        {dossier.aangeboden.length > 0 ? (
          <ul className="wp-schade-lijst" data-aangeboden>
            {dossier.aangeboden.map((keuze, i) => (
              <li key={i}>{keuze}</li>
            ))}
          </ul>
        ) : (
          <p className="wp-leegregel">Er zijn nog geen keuzes aangeboden.</p>
        )}

        <p className="wp-schade-keuze" data-keuze-klant>
          <b>Keuze van de klant:</b>{" "}
          {dossier.keuze_klant ?? (
            <span className="wp-leegregel">de klant heeft nog niet gekozen</span>
          )}
        </p>
        {dossier.reden_jp ? (
          <p className="wp-schade-keuze">
            <b>Waarom dit bij jou ligt:</b> {dossier.reden_jp}
          </p>
        ) : null}
      </div>
    </div>
  );
}
